<template>
  <div class="ChatAssistantLiveStep" role="status" aria-label="Loading">
    <UIThinkingOrb class="ChatAssistantLiveStep__orb" />
    <!--
      Hidden from assistive tech like `UIChatTypingIndicator`'s label: it changes every few
      seconds, and a live region announcing each new wording is unusable with a screen reader.
    -->
    <UIAnimatedText
      class="ChatAssistantLiveStep__label"
      :text="`${currentLabel}…`"
      aria-hidden="true"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { useChatWaitLabel } from '@/composables/useChatWaitLabel'
import type { ChatStreamPhase } from '@/composables/useEventStream'
import UIThinkingOrb from '@/views/components/ui/indicators/UIThinkingOrb.vue'
import UIAnimatedText from '@/views/components/ui/misc/UIAnimatedText.vue'

export interface Props {
  /** What the reply is doing right now, e.g. "Searching the web". Without it the copy rotates. */
  label?: string
  /** Latest `stream_progress` milestone, for the rotating copy. */
  streamPhase?: ChatStreamPhase
  /** Reasoning chunks are streaming. */
  isReasoning?: boolean
  /** Shows the retry notice instead of the rotating copy. */
  isRetrying?: boolean
  /**
   * When the reply's rotating copy started (epoch ms). The row under the user's message and the
   * one ending the steps list share it, so the copy carries on when the row moves between them.
   */
  waitStartedAt?: number
}

const props = defineProps<Props>()

// The rotation keeps running under a fixed label and counts from the reply's own start, so a
// wait that resumes after a step picks up where the copy had got to instead of starting over.
const { statusLabel } = useChatWaitLabel({
  streamPhase: () => props.streamPhase,
  isReasoning: () => props.isReasoning,
  isRetrying: () => props.isRetrying,
  rotationStartedAt: () => props.waitStartedAt,
})

const currentLabel = computed(() => props.label ?? statusLabel.value)
</script>

<style lang="scss" scoped>
.ChatAssistantLiveStep {
  @include font-body-small-regular;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  // Fills the 1.25rem the step icons take, so its label lines up with theirs.
  .ChatAssistantLiveStep__orb {
    --orb-size: 1.25rem;
  }

  // Breathes in step with the orb, so the row reads as one live signal.
  .ChatAssistantLiveStep__label {
    min-width: 0;
    color: $color-neutral-140;
    animation: ChatAssistantLiveStep-text-pulse 1.6s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .ChatAssistantLiveStep__label {
      animation: none;
    }
  }
}

@keyframes ChatAssistantLiveStep-text-pulse {
  50% {
    opacity: 0.45;
  }
}
</style>
